/**
 * Client-side validation utilities
 * Provides comprehensive validation functions with detailed error messages
 */

import i18n from "../i18n/config";
import { eLimiteDelPiano } from "./limitiPiano";

// Common validation patterns
export const patterns = {
  email: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
  url: /^https?:\/\/.+/,
  mongoId: /^[0-9a-fA-F]{24}$/,
  filename: /^[^<>:"/\\|?*\x00-\x1f]+$/,
  // Come l'API: accenti, apostrofi e "&" si', markup e caratteri invisibili no.
  botName: /^[^<>{}`\u0000-\u001F\u007F]+$/u,
};

// Validation functions (messaggi in italiano, come il resto delle finestre;
// fieldName e' il nome del campo con l'articolo: "Il titolo", "L'URL")
export const validators = {
  // Required field validation
  required: (value, fieldName = "Il campo") => {
    if (!value || (typeof value === "string" && value.trim().length === 0)) {
      return `${fieldName} è obbligatorio`;
    }
    return null;
  },

  // String length validation
  minLength: (value, min, fieldName = "Il campo") => {
    if (value && value.length < min) {
      return `${fieldName} deve avere almeno ${min} caratteri`;
    }
    return null;
  },

  maxLength: (value, max, fieldName = "Il campo") => {
    if (value && value.length > max) {
      return `${fieldName} può avere al massimo ${max} caratteri`;
    }
    return null;
  },

  // Number validation
  minValue: (value, min, fieldName = "Field") => {
    const num = Number(value);
    if (!isNaN(num) && num < min) {
      return `${fieldName} must be at least ${min}`;
    }
    return null;
  },

  maxValue: (value, max, fieldName = "Field") => {
    const num = Number(value);
    if (!isNaN(num) && num > max) {
      return `${fieldName} must be less than or equal to ${max}`;
    }
    return null;
  },

  // Pattern validation
  pattern: (value, pattern, message) => {
    if (value && !pattern.test(value)) {
      return message;
    }
    return null;
  },

  // Email validation
  email: (value) => {
    return validators.pattern(
      value,
      patterns.email,
      "Please enter a valid email address"
    );
  },

  // URL validation
  url: (value) => {
    if (!value) return null;

    try {
      const url = new URL(value);
      if (!["http:", "https:"].includes(url.protocol)) {
        return "URL must use HTTP or HTTPS protocol";
      }
      return null;
    } catch {
      return "Please enter a valid URL";
    }
  },

  // Bot name validation
  botName: (value) => {
    if (!value || value.trim().length === 0) {
      return "Il nome dell'assistente è obbligatorio";
    }

    const trimmed = value.trim();

    if (trimmed.length > 100) {
      return "Il nome può avere al massimo 100 caratteri";
    }

    if (!patterns.botName.test(trimmed)) {
      return "Il nome non può contenere < > { } ` o caratteri di controllo";
    }

    return null;
  },

  // Filename validation
  filename: (value) => {
    if (!value) return null;

    if (value.length > 255) {
      return "Filename must be less than 255 characters";
    }

    if (!patterns.filename.test(value)) {
      return "Filename contains invalid characters";
    }

    return null;
  },

  // File size validation (in bytes)
  fileSize: (size, maxSizeMB = 50) => {
    const maxBytes = maxSizeMB * 1024 * 1024;
    if (size > maxBytes) {
      return `File size must be less than ${maxSizeMB}MB`;
    }
    return null;
  },

  // MIME type validation
  mimeType: (type, allowedTypes = []) => {
    if (allowedTypes.length > 0 && !allowedTypes.includes(type)) {
      return `File type must be one of: ${allowedTypes.join(", ")}`;
    }
    return null;
  },

  // Text content validation (enforces the content guidelines shown in the UI)
  textContent: (value) => {
    if (!value) return null;

    const trimmed = value.trim();

    if (trimmed.length < 50) {
      return "Il contenuto deve avere almeno 50 caratteri";
    }

    if (trimmed.length > 100000) {
      return "Il contenuto non può superare i 100.000 caratteri";
    }

    return null;
  },

  // Refresh days validation
  refreshDays: (value) => {
    const num = Number(value);

    if (isNaN(num)) {
      return "Refresh interval must be a number";
    }

    if (num < 1) {
      return "Refresh interval must be at least 1 day";
    }

    if (num > 365) {
      return "Refresh interval must be less than 365 days";
    }

    return null;
  },

  // Query text validation
  queryText: (value) => {
    if (!value) return null;

    const trimmed = value.trim();

    if (trimmed.length < 3) {
      return "Query must be at least 3 characters long";
    }

    if (trimmed.length > 1000) {
      return "Query must be less than 1000 characters";
    }

    return null;
  },
};

// Validation rule builder
export const createValidationRules = (rules) => {
  return (value, allValues = {}) => {
    for (const rule of rules) {
      const error = rule(value, allValues);
      if (error) {
        return error;
      }
    }
    return null;
  };
};

// Common validation rule sets
export const validationRules = {
  botName: createValidationRules([
    (value) => validators.required(value, "Il nome dell'assistente"),
    validators.botName,
  ]),

  sourceUrl: createValidationRules([
    (value) => validators.required(value, "L'URL"),
    validators.url,
    (value) => validators.maxLength(value, 2048, "L'URL"),
  ]),

  sourceText: createValidationRules([
    (value) => validators.required(value, "Il testo"),
    validators.textContent,
  ]),

  sourceTitle: createValidationRules([
    (value) => validators.required(value, "Il titolo"),
    (value) => validators.minLength(value, 1, "Il titolo"),
    (value) => validators.maxLength(value, 200, "Il titolo"),
  ]),

  refreshDays: createValidationRules([
    (value) => validators.required(value, "L'intervallo di aggiornamento"),
    validators.refreshDays,
  ]),

  queryText: createValidationRules([
    (value) => validators.required(value, "La domanda"),
    validators.queryText,
  ]),

  filename: createValidationRules([
    (value) => validators.required(value, "Filename"),
    validators.filename,
  ]),
};

// Real-time validation hook
export const useRealTimeValidation = (
  value,
  validationRule,
  debounceMs = 300
) => {
  const [error, setError] = React.useState(null);
  const [isValidating, setIsValidating] = React.useState(false);

  React.useEffect(() => {
    if (!validationRule) return;

    setIsValidating(true);

    const timeoutId = setTimeout(() => {
      const validationError = validationRule(value);
      setError(validationError);
      setIsValidating(false);
    }, debounceMs);

    return () => {
      clearTimeout(timeoutId);
      setIsValidating(false);
    };
  }, [value, validationRule, debounceMs]);

  return { error, isValidating };
};

// Form validation helper
export const validateForm = (values, rules) => {
  const errors = {};

  for (const [field, rule] of Object.entries(rules)) {
    const error = rule(values[field], values);
    if (error) {
      errors[field] = error;
    }
  }

  return {
    errors,
    isValid: Object.keys(errors).length === 0,
  };
};

// API error parser. Il client (api/client.js) ha gia' messo in error.message
// il testo tradotto dei limiti del piano: quello va mostrato, non il
// messaggio inglese dell'API.
export const parseApiError = (error) => {
  if (eLimiteDelPiano(error)) {
    return { message: error.message, type: "limit" };
  }

  // Handle network errors
  if (!error.response) {
    return {
      message: i18n.t("errors.api.network"),
      type: "network",
    };
  }

  const { status, data } = error.response;

  // Handle validation errors
  if (status === 400 && data.details) {
    return {
      message: data.message || "Validation failed",
      type: "validation",
      fieldErrors: data.details.reduce((acc, detail) => {
        acc[detail.field] = detail.message;
        return acc;
      }, {}),
    };
  }

  // Handle rate limiting
  if (status === 429) {
    const retryAfter = data.details?.retryAfter || 60;
    return {
      message: i18n.t("errors.api.tooManyRequests"),
      type: "rate_limit",
      retryAfter,
    };
  }

  // Handle authentication errors
  if (status === 401) {
    return {
      message: i18n.t("errors.api.unauthorized"),
      type: "auth",
    };
  }

  // Handle authorization errors
  if (status === 403) {
    return {
      message: i18n.t("errors.api.forbidden"),
      type: "forbidden",
    };
  }

  // Handle not found errors
  if (status === 404) {
    return {
      message: i18n.t("errors.api.notFound"),
      type: "not_found",
    };
  }

  // Handle server errors
  if (status >= 500) {
    return {
      message: i18n.t("errors.api.server"),
      type: "server",
    };
  }

  // Generic error
  return {
    message: error.message || data.message || i18n.t("errors.api.generic"),
    type: "generic",
  };
};

export default {
  patterns,
  validators,
  validationRules,
  createValidationRules,
  useRealTimeValidation,
  validateForm,
  parseApiError,
};
